import { Button, Badge } from '@mantine/core'
import { type CoverageStatusLabel, getCoverageStatusColor } from '@/utils/eligibilityStatus'

type InsuranceSummary = {
  provider?: string
  memberId?: string
  rank?: string
  type?: string
  showSubscriberDetails?: boolean
  subscriberName?: string
  subscriberDob?: string
  subscriberMemberId?: string
  coverageStatus?: CoverageStatusLabel
  approveLoading?: boolean
  disapproveLoading?: boolean
  lastCheckDate?: string
}

type InsuranceSummaryCardProps = {
  insurance: InsuranceSummary
  onEdit: () => void
  onCheckEligibility: () => void
  onApprove?: () => void
  onDisapprove?: () => void
  showReviewActions?: boolean
}

export function InsuranceSummaryCard({
  insurance,
  onEdit,
  onCheckEligibility,
  onApprove,
  onDisapprove,
  showReviewActions = true,
}: InsuranceSummaryCardProps) {
  const approveLoading = insurance.approveLoading
  const disapproveLoading = insurance.disapproveLoading

  const coverageStatusLabel = insurance.coverageStatus || 'Unknown'
  const coverageStatusColor = getCoverageStatusColor(coverageStatusLabel)

  return (
    <div className="flex min-h-[260px] w-[400px] flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2 text-lg font-semibold">
        {insurance.rank ? `${insurance.rank} ` : ''}Insurance
      </div>
      <div className="flex flex-col gap-1 text-sm">
        <div>
          <b>Insurance provider:</b> {insurance.provider || '-'}
        </div>
        <div>
          <b>Member ID:</b> {insurance.memberId || '-'}
        </div>
        <div>
          <b>Insurance rank:</b> {insurance.rank || '-'}
        </div>
        <div>
          <b>Coverage type:</b> {insurance.type || '-'}
        </div>
        {insurance.showSubscriberDetails && (
          <>
            <div>
              <b>Subscriber Name:</b> {insurance.subscriberName || '-'}
            </div>
            <div>
              <b>Subscriber DOB:</b> {insurance.subscriberDob || '-'}
            </div>
            <div>
              <b>Subscriber Member ID:</b> {insurance.subscriberMemberId || '-'}
            </div>
          </>
        )}
        <div className="flex items-center gap-2">
          <b>Status:</b>
          <Badge color={coverageStatusColor} variant="filled">
            {coverageStatusLabel}
          </Badge>
        </div>
        <div>
          <b>Last eligibility check date:</b> {insurance.lastCheckDate || '-'}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="xs" variant="outline" onClick={onEdit}>
          Edit
        </Button>
        <Button size="xs" variant="outline" onClick={onCheckEligibility}>
          Check Eligibility
        </Button>
        {showReviewActions && onApprove && onDisapprove && (
          <>
            <Button
              size="xs"
              color="green"
              variant="outline"
              onClick={onApprove}
              loading={approveLoading}
            >
              Approve
            </Button>
            <Button
              size="xs"
              color="red"
              variant="outline"
              onClick={onDisapprove}
              loading={disapproveLoading}
            >
              Disapprove
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
